import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@components/common'
import { useProjectWizardStore } from '@store/useProjectWizardStore'
import certificateService from '@services/certificateService'
import roleService from '@services/roleService'
import projectDocumentService from '@services/projectDocumentService'
import '../ProjectWizard.scss'

/**
 * Step 4: Review & Confirmation
 *
 * Shows summary of all entered data before project creation
 */
const ReviewStep = ({ onValidChange, onEdit }) => {
	const { basicDetails, projectScope, documents } = useProjectWizardStore()

	const [certificates, setCertificates] = useState([])
	const [roles, setRoles] = useState([])
	const [loading, setLoading] = useState(true)

	// Fetch certificate and role details for display
	useEffect(() => {
		const fetchDetails = async () => {
			setLoading(true)
			try {
				// Fetch certificates
				if (basicDetails.certificates.length > 0) {
					const certResponse = await certificateService.getByIds(basicDetails.certificates)
					setCertificates(certResponse.data || [])
				}

				// Fetch roles for associated roles
				const associatedRoles = projectScope.associatedRoles || []
				if (associatedRoles.length > 0) {
					const roleResponse = await roleService.getCompanyAssignableRoles()
					const allRoles = roleResponse.data || []
					setRoles(allRoles.filter((r) => associatedRoles.includes(r.id)))
				}
			} catch (error) {
				console.error('Failed to fetch details:', error)
			} finally {
				setLoading(false)
			}
		}
		fetchDetails()
	}, [basicDetails.certificates, projectScope.associatedRoles])

	// Review step is always valid if we got here
	useEffect(() => {
		onValidChange && onValidChange(true)
	}, [onValidChange])

	// Format date for display
	const formatDate = (dateString) => {
		if (!dateString) return 'Not set'
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		})
	}

	if (loading) {
		return (
			<div className="wizard-step">
				<div className="wizard-step__loading">Loading review...</div>
			</div>
		)
	}

	return (
		<div className="wizard-step">
			<div className="wizard-step__header">
				<h2 className="wizard-step__title">Review & Confirm</h2>
				<p className="wizard-step__description">
					Review all information before creating your project
				</p>
			</div>

			<div className="wizard-step__content">
				{/* Basic Details Section */}
				<div className="review-section">
					<div className="review-section__header">
						<h3 className="review-section__title">
							<i className="icon-doc" /> Basic Details
						</h3>
						<Button
							variant="ghost"
							size="small"
							onClick={() => onEdit && onEdit(1)}
						>
							<i className="icon-pencil" /> Edit
						</Button>
					</div>
					<div className="review-section__content">
						<div className="review-grid">
							<div className="review-item review-item--full">
								<span className="review-item__label">Project Name</span>
								<span className="review-item__value">{basicDetails.name}</span>
							</div>
							{basicDetails.description && (
								<div className="review-item review-item--full">
									<span className="review-item__label">Description</span>
									<span className="review-item__value">{basicDetails.description}</span>
								</div>
							)}
							<div className="review-item review-item--full">
								<span className="review-item__label">Compliance Standards</span>
								<div className="review-item__tags">
									{certificates.map((cert) => (
										<span key={cert.id} className="tag tag--primary">
											{cert.title}
										</span>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Project Scope Section */}
				<div className="review-section">
					<div className="review-section__header">
						<h3 className="review-section__title">
							<i className="icon-target" /> Project Scope
						</h3>
						<Button
							variant="ghost"
							size="small"
							onClick={() => onEdit && onEdit(2)}
						>
							<i className="icon-pencil" /> Edit
						</Button>
					</div>
					<div className="review-section__content">
						<div className="review-grid">
							<div className="review-item">
								<span className="review-item__label">Industry</span>
								<span className="review-item__value">
									{projectScope.industry || 'Not specified'}
								</span>
							</div>
							<div className="review-item">
								<span className="review-item__label">Domain</span>
								<span className="review-item__value">
									{projectScope.domain || 'Not specified'}
								</span>
							</div>
							<div className="review-item">
								<span className="review-item__label">Start Date</span>
								<span className="review-item__value">
									{formatDate(projectScope.startDate)}
								</span>
							</div>
							<div className="review-item">
								<span className="review-item__label">Target Date</span>
								<span className="review-item__value">
									{formatDate(projectScope.targetDate)}
								</span>
							</div>
							{projectScope.technologyStack.length > 0 && (
								<div className="review-item review-item--full">
									<span className="review-item__label">Technology Stack</span>
									<div className="review-item__tags">
										{projectScope.technologyStack.map((tech) => (
											<span key={tech} className="tag">
												{tech}
											</span>
										))}
									</div>
								</div>
							)}
							{projectScope.geographicalLocations.length > 0 && (
								<div className="review-item review-item--full">
									<span className="review-item__label">Geographical Locations</span>
									<div className="review-item__tags">
										{projectScope.geographicalLocations.map((loc) => (
											<span key={loc} className="tag">
												{loc}
											</span>
										))}
									</div>
								</div>
							)}
							{roles.length > 0 && (
								<div className="review-item review-item--full">
									<span className="review-item__label">Associated Roles</span>
									<div className="review-item__tags">
										{roles.map((role) => (
											<span key={role.id} className="tag tag--secondary">
												{role.title || role.name}
											</span>
										))}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Documents Section */}
				<div className="review-section">
					<div className="review-section__header">
						<h3 className="review-section__title">
							<i className="icon-folder" /> Documents
						</h3>
						<Button
							variant="ghost"
							size="small"
							onClick={() => onEdit && onEdit(3)}
						>
							<i className="icon-pencil" /> Edit
						</Button>
					</div>
					<div className="review-section__content">
						{documents.length > 0 ? (
							<div className="review-documents">
								{documents.map((doc) => (
									<div key={doc.id} className="review-document">
										<i className="icon-doc review-document__icon" />
										<div className="review-document__info">
											<span className="review-document__name">{doc.name}</span>
											<span className="review-document__meta">
												{projectDocumentService.getTypeLabel(doc.type)} &middot;{' '}
												{projectDocumentService.formatFileSize(doc.size)}
											</span>
										</div>
									</div>
								))}
							</div>
						) : (
							<p className="review-empty">No documents uploaded</p>
						)}
					</div>
				</div>

				{/* Info Box */}
				<div className="info-box info-box--primary">
					<i className="icon-bulb info-box__icon" />
					<div className="info-box__content">
						<strong>What happens next?</strong>
						<p>
							After confirming, your project will be created and AI will generate a
							comprehensive compliance checklist based on the selected standards and
							scope. You can review and customize the checklist before finalizing.
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}

ReviewStep.propTypes = {
	onValidChange: PropTypes.func,
	onEdit: PropTypes.func,
}

export default ReviewStep
